import React, { useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  VStack,
  Text,
  Button,
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { Formik, Form } from "formik";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { MapPinned, MapPin } from "lucide-react";
import { Input } from "../../../components/Input/Input";
import { ILocationFormValues } from "../types/";
import { InvalidGeofenceAlert } from "pages/ForecastChart/ForecastChartPanelMap/InvalidGeofenceAlert";
import { GeofenceMapSelector } from "../../../components/Map";
import { useStores } from "stores/setup/use-store";

interface LocationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ILocationFormValues) => void;
  initialValues: ILocationFormValues;
  validationSchema: any;
  isEditing: boolean;
  isLoading?: boolean;
  handleDrawingMode?: (formValues?: ILocationFormValues) => void;
  handleGeoJsonLoaded?: (geojson: any) => void;
  clearGeofence?: () => void;
  selectedGeofence?: any[];
  setIsGeofenceLoadedByGeoJson?: (value: boolean) => void;
  showGeofenceError?: boolean;
  isGeofenceInvalid?: boolean;
  isDropZoneVisible?: boolean;
  setIsDropZoneVisible?: (value: boolean) => void;
}

export const LocationDrawer: React.FC<LocationDrawerProps> = observer(
  ({
    isOpen,
    onClose,
    onSubmit,
    initialValues,
    validationSchema,
    isEditing,
    isLoading = false,
    handleDrawingMode,
    handleGeoJsonLoaded,
    clearGeofence,
    selectedGeofence,
    setIsGeofenceLoadedByGeoJson,
    showGeofenceError,
    isGeofenceInvalid,
    isDropZoneVisible,
    setIsDropZoneVisible,
  }) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "locations",
    });

    const {
      dataStores: { locationStore },
    } = useStores();

    return (
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({
            handleSubmit,
            setFieldValue,
            values,
            errors,
            touched,
            handleChange,
          }) => {
            useEffect(() => {
              locationStore.setCreateFormValues(values);
            }, [values]);

            const handleSubmitForm = (e: React.FormEvent) => {
              e.preventDefault();
              handleSubmit();
            };
            return (
              <>
                <DrawerOverlay />
                <DrawerContent bg="white">
                  <DrawerCloseButton color={"black"} />
                  <DrawerHeader>
                    <Heading
                      display={"flex"}
                      size="md"
                      color="black"
                      gap="16px"
                    >
                      <MapPin size="24px" />
                      <Text mt={"3px"}>
                        {isEditing ? t("editLocation") : t("createLocation")}
                      </Text>
                    </Heading>
                  </DrawerHeader>
                  <DrawerBody color="black">
                    {isLoading ? (
                      <Center h="100%" w="100%" minH="400px">
                        <Spinner
                          speed="0.65s"
                          emptyColor="gray.300"
                          color="newBlue.500"
                          size="xl"
                        />
                      </Center>
                    ) : (
                      <Form onSubmit={handleSubmit}>
                        <VStack spacing={6} align="stretch">
                          <FormControl
                            isRequired
                            isInvalid={!!errors.name && touched.name}
                          >
                            <FormLabel color="#414651" fontWeight={"500"}>
                              {t("locationName")}
                            </FormLabel>
                            <Input
                              name="name"
                              placeholder={
                                t("locationNamePlaceholder") as string
                              }
                              value={values.name}
                              onChange={handleChange}
                              maxLength={70}
                            />
                            <FormErrorMessage mt={4}>
                              {t("requiredField")}
                            </FormErrorMessage>
                          </FormControl>

                          <Box borderWidth="1px" borderRadius="md" p={4}>
                            <Flex gap="12px" mb="16px">
                              <MapPinned color="#086AA9" size="20px" />
                              <Text
                                fontWeight={"400"}
                                fontSize="md"
                                color="#086AA9"
                              >
                                {t("monitoredArea")}
                              </Text>
                            </Flex>

                            {showGeofenceError && (
                              <Box mb={2}>
                                <InvalidGeofenceAlert t={t} />
                              </Box>
                            )}

                            <FormControl
                              isRequired
                              isInvalid={
                                !!errors.coordinates && !!touched.coordinates
                              }
                            >
                              <GeofenceMapSelector
                                coordinates={values.coordinates}
                                selectedGeofence={selectedGeofence}
                                t={t}
                                handleDrawingMode={handleDrawingMode}
                                handleGeoJsonLoaded={handleGeoJsonLoaded}
                                clearGeofence={clearGeofence}
                                onCoordinatesChange={(coordinates) =>
                                  setFieldValue("coordinates", coordinates)
                                }
                                setIsGeofenceLoadedByGeoJson={
                                  setIsGeofenceLoadedByGeoJson
                                }
                                formValues={{
                                  name: values.name,
                                  coordinatesType: values.coordinatesType,
                                  coordinates: values.coordinates || [],
                                }}
                                hideSaveAreaButtonOnMap={true}
                                showSelectSavedAreaButton={false}
                                isDropZoneVisible={isDropZoneVisible}
                                setIsDropZoneVisible={setIsDropZoneVisible}
                              />
                              <FormErrorMessage mt={4}>
                                {t("pleaseDrawAnArea")}
                              </FormErrorMessage>
                            </FormControl>
                          </Box>
                        </VStack>
                      </Form>
                    )}
                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px" bg="white">
                    <Button
                      variant="ghost"
                      color="newBlue.500"
                      mr={3}
                      onClick={onClose}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      layerStyle="ocean"
                      _hover={{ layerStyle: "ocean" }}
                      isLoading={isLoading}
                      loadingText={
                        isEditing
                          ? (t("saveChanges") as string)
                          : (t("createLocationButton") as string)
                      }
                      disabled={isGeofenceInvalid}
                      onClick={handleSubmitForm}
                    >
                      {isEditing ? t("saveChanges") : t("createLocationButton")}
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </>
            );
          }}
        </Formik>
      </Drawer>
    );
  }
);
